import { OrganizationType } from '../hooks/useAccessBadge';
import { InsightsLens } from '../hooks/useLens';

// Where `/[tenantId]/insights` forwards a viewer, or null when there's nothing
// to forward to. Pure so the permutations that have bitten the landing page — a
// teacher with no org, a viewer whose id hasn't resolved, an org whose `type` is
// missing — are testable without a session, a router, or an Unleash provider.
// A null result is what the page turns into a 404 once the access badge has
// resolved; returning null rather than a half-formed URL is what keeps a viewer
// off a never-resolving spinner.
//
// Lives here rather than beside the page because everything under `pages/` is a
// route in the Next pages router — a colocated test file there is built as a
// page and fails the production build.
export function insightsLandingDestination({
  tenantId,
  org,
  isTeacherView,
  viewerId,
  suffix,
}: {
  tenantId: string | undefined;
  org: { id: string; type?: OrganizationType } | undefined;
  isTeacherView: boolean;
  viewerId: string | null;
  suffix: string;
}): string | null {
  if (!tenantId || !org) return null;
  // Teachers always land inside their own org on their own teaching — never the
  // parent district, which their dashboard doesn't cover.
  if (isTeacherView) {
    const paths = insightsScopePaths({ tenantId, orgId: org.id, viewerId });
    return paths ? `${paths.teaching}${suffix}` : null;
  }
  const scope = scopeForOrgType(org.type);
  return scope ? `/${tenantId}/insights/${scope}/${org.id}${suffix}` : null;
}

// The two scopes a teacher's dashboard covers, as bare paths. The sidebar
// renders them as sub-items and the landing page forwards to `teaching`, so
// they resolve here rather than being spelled out at each call site — a teacher
// arriving from the nav and a teacher arriving from `/insights` must land on
// the same URL, or the sidebar's active state won't match the page they're on.
//
// Paths, NOT hrefs: the caller appends `querySuffix()` for the link. The
// sidebar also compares the current path against `school` to decide which item
// is active, and folding the suffix in here made that comparison fail for any
// non-default period, rubric or lens — the school item never lit up. Keeping
// the query out of the returned value removes that failure mode rather than
// papering over it at the comparison.
//
// Null when we can't name both: no tenant, no organization on the badge, or a
// viewer id that hasn't resolved yet. Callers hide the sub-items rather than
// rendering a half-formed link.
export function insightsScopePaths({
  tenantId,
  orgId,
  viewerId,
}: {
  tenantId: string | undefined;
  orgId: string | undefined;
  viewerId: string | null;
}): { teaching: string; school: string } | null {
  if (!tenantId || !orgId || !viewerId) return null;
  return {
    teaching: `/${tenantId}/insights/school/${orgId}/${viewerId}`,
    school: `/${tenantId}/insights/school/${orgId}`,
  };
}

// Where each Insights tab opens. Growth, Gaps and Skill gaps are separate pages,
// not views of the current scope: a tab opens at its own top level, never at
// the school or teacher the viewer is on. That top level is the page's root org
// (`root.growthPath`) when the page can name it — which keeps an internal admin
// inside the district they're browsing — else the `/insights` landing, which
// forwards `?lens=` to the viewer's own top level. A teacher's tabs stay in the
// view they're on instead (`teacherViewPath`: My school or My teaching), so a
// tab never takes them off My school. Skill gaps opens on its picker prompt,
// carrying the root org only, never a school or teacher pick.
//
// `suffix` is `useTimePeriod().querySuffix`, so the period and an ECR rubric
// travel with every tab; an empty value drops that key.
export function insightsTabHrefs({
  tenantId,
  isTeacherView,
  root,
  teacherViewPath,
  suffix,
}: {
  tenantId: string;
  isTeacherView: boolean;
  root?: { growthPath?: string; org?: string };
  // A teacher's view, from `insightsScopePaths`; unknown until the badge loads.
  teacherViewPath?: string;
  suffix: (extra: Record<string, string>) => string;
}): Record<InsightsLens, string> {
  const growthPath =
    (isTeacherView ? teacherViewPath : root?.growthPath) ?? `/${tenantId}/insights`;
  const org = isTeacherView ? '' : root?.org ?? '';
  return {
    growth: `${growthPath}${suffix({ lens: '' })}`,
    gaps: `${growthPath}${suffix({ lens: 'gaps' })}`,
    students: `/${tenantId}/insights/students${suffix({ lens: '', org })}`,
  };
}

// Where the tabs open from a school's pages (the school and its teachers'): the
// school's district — unless the viewer's own org is this school. That's a
// school admin, whose Insights tops out at the school and who can't open the
// district. Only the badge tells which, and only the school's data names the
// district, so until both load this is undefined and the tabs take the
// `/insights` landing rather than guess.
export function schoolTabsRoot({
  tenantId,
  schoolId,
  parentOrgId,
  viewerOrgId,
}: {
  tenantId: string;
  schoolId: string;
  // The school's district (null: it has none); undefined until the school loads.
  parentOrgId: string | null | undefined;
  // The viewer's own org (null: none); undefined until the badge loads.
  viewerOrgId: string | null | undefined;
}): { growthPath: string; org: string } | undefined {
  if (parentOrgId === undefined || viewerOrgId === undefined) return undefined;
  return parentOrgId && viewerOrgId !== schoolId
    ? { growthPath: `/${tenantId}/insights/district/${parentOrgId}`, org: parentOrgId }
    : { growthPath: `/${tenantId}/insights/school/${schoolId}`, org: schoolId };
}

// Whether the Skill gaps page's scope org is a district — or null while that
// isn't known yet. Nothing is known before the access badge: only it says
// whether `?org=` (which every Insights tab carries) is the viewer's own org.
// The viewer's own org's kind comes from the badge. Another org (an internal
// admin's) is a district when its picker list has child schools, so it waits
// on that list; a district whose schools taught no one in the period reads as
// a school there.
export function skillGapsRootIsDistrict({
  orgParam,
  badgeOrg,
  schools,
}: {
  orgParam: string | undefined;
  badgeOrg: { id: string; type?: OrganizationType } | undefined;
  // The scope's child schools; undefined until the scope has loaded.
  schools: { id: string }[] | undefined;
}): boolean | null {
  if (!badgeOrg) return null;
  if (!orgParam || orgParam === badgeOrg.id) {
    return badgeOrg.type === OrganizationType.District;
  }
  return schools ? schools.length > 0 : null;
}

// Whether two internal URLs name the same page in the same state — same path,
// same query in any order. Back uses it to tell whether the page above is the
// one the browser would go back to.
export function isSameInsightsUrl(a: string, b: string): boolean {
  const normalize = (url: string) => {
    const [path, query = ''] = url.split('#')[0].split('?');
    const params = [...new URLSearchParams(query).entries()].sort(([x], [y]) =>
      x.localeCompare(y),
    );
    return `${path.replace(/\/$/, '')}?${new URLSearchParams(params).toString()}`;
  };
  return normalize(a) === normalize(b);
}

// A student's grade as a phrase: "4th" reads "4th grade", a bare "4" reads
// "Grade 4", and a label that already says what it is ("Kindergarten",
// "6th Grade") stays as it is — never "Grade 4th".
export function gradePhrase(grade: string): string {
  const g = grade.trim();
  if (/^\d+$/.test(g)) return `Grade ${g}`;
  if (/^\d+(st|nd|rd|th)$/i.test(g)) return `${g} grade`;
  return g;
}

const scopeForOrgType = (
  type: OrganizationType | undefined,
): 'district' | 'school' | null => {
  if (!type) return null;
  if (type === OrganizationType.District) return 'district';
  // Everything that isn't a District — School, `Other` (e.g. Curipod-source
  // demo orgs like "Curipod Texas Academy"), or any value the backend
  // started returning that we don't model yet — routes to the school view.
  // The school page handles missing/sparse data gracefully, so it's a
  // better default than stranding a viewer with a valid org on a 404.
  return 'school';
};
